"use client"

import { useEffect, useMemo, useState } from "react"
import { getCoupons, createCoupon as createCouponAPI, bulkCreateCoupons, tactosCreateCoupon, tactosGetCoupons, tactosDeleteCoupon } from "../utils/api"
import * as XLSX from "xlsx"

export default function TactosCouponManagement() {
    const [coupons, setCoupons] = useState([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [couponCode, setCouponCode] = useState("")
    const [discount, setDiscount] = useState("")
    const [expiryDate, setExpiryDate] = useState("")
    const [isActive, setIsActive] = useState(true)
    const [searchTerm, setSearchTerm] = useState("")
    const [filterBy, setFilterBy] = useState("All")
    const [showToast, setShowToast] = useState(false)
    const [toastMessage, setToastMessage] = useState("")
    const [toastType, setToastType] = useState("success")
    const [loading, setLoading] = useState(false)

    // Pagination
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)

    const showToastMessage = (message, type = "success") => {
        setToastMessage(message)
        setToastType(type)
        setShowToast(true)
        setTimeout(() => setShowToast(false), 3000)
    }

    const normalize = (c) => {
        const id = c._id || c.id
        const createdAt = c.createdAt || new Date()
        const createdDate = new Date(createdAt).toLocaleDateString()
        const createdTime = new Date(createdAt).toLocaleTimeString()

        return {
            id,
            code: (c.code || "").toUpperCase(),
            discount: c.discount || 0,
            expiryDate: c.expiryDate ? new Date(c.expiryDate).toLocaleDateString() : "N/A",
            status: c.isActive ? "active" : "expired",
            isActive: c.isActive,
            createdDate,
            createdTime,
            raw: c,
        }
    }

    const fetchCoupons = async () => {
        try {
            setLoading(true)
            const data = await tactosGetCoupons()
            const arr = Array.isArray(data) ? data : data.data || data.coupons || []
            setCoupons(arr.map(normalize))
        } catch (err) {
            console.error(err)
            showToastMessage(err?.message || "Failed to fetch coupons", "error")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchCoupons()
    }, [])

    useEffect(() => {
        setCurrentPage(1)
    }, [searchTerm, filterBy, itemsPerPage, coupons.length])

    const handleDeleteCoupon = async (id) => {
        if (!confirm("Are you sure you want to delete this coupon?")) return
        try {
            // Call your API (make sure you have deleteCoupon in utils/api)
            await tactosDeleteCoupon(id)
            setCoupons((prev) => prev.filter((c) => c.id !== id))
            showToastMessage("Coupon deleted successfully")
        } catch (err) {
            console.error(err)
            showToastMessage(err?.message || "Failed to delete coupon", "error")
        }
    }


    // Manual Add
    const handleManualSubmit = async (e) => {
        e.preventDefault()
        if (!couponCode.trim()) {
            showToastMessage("Please enter a coupon code", "error")
            return
        }

        const payload = {
            code: couponCode.toUpperCase(),
            discount: Number(discount) || 0,
            expiryDate: expiryDate ? new Date(expiryDate) : null,
            isActive,
        }

        try {
            const created = await tactosCreateCoupon(payload)
            const createdDoc = created && created._id ? created : created.data || created
            setCoupons((prev) => [...prev, normalize(createdDoc)])
            setCouponCode("")
            setDiscount("")
            setExpiryDate("")
            setIsModalOpen(false)
            showToastMessage("Coupon added successfully")
        } catch (err) {
            console.error(err)
            showToastMessage(err?.message || "Failed to create coupon", "error")
        }
    }

    // Filters
    const filteredCoupons = useMemo(() => {
        const q = searchTerm.trim().toLowerCase()
        return coupons.filter((c) => {
            const matchesSearch = c.code.toLowerCase().includes(q)
            const matchesFilter = filterBy === "All" || c.status === filterBy.toLowerCase()
            return matchesSearch && matchesFilter
        })
    }, [coupons, searchTerm, filterBy])

    // Pagination
    const totalItems = filteredCoupons.length
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
    const currentPageSafe = Math.min(currentPage, totalPages)
    const startIndex = (currentPageSafe - 1) * itemsPerPage
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
    const paginatedCoupons = filteredCoupons.slice(startIndex, endIndex)

    return (
        <div className="p-6">
            {/* Toast */}
            {showToast && (
                <div
                    className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toastType === "error" ? "bg-red-500" : "bg-green-500"
                        } text-white`}
                >
                    {toastMessage}
                </div>
            )}

            {/* Top Container */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Tactos Coupon Management</h1>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow"
                >
                    + Add Coupon
                </button>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4">Add Coupon</h2>
                        <form onSubmit={handleManualSubmit} className="space-y-4">
                            <div>
                                <label>Coupon Code</label>
                                <input
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value)}
                                    className="w-full border px-3 py-2 rounded"
                                />
                            </div>
                            <div>
                                <label>Discount (%)</label>
                                <input
                                    type="number"
                                    value={discount}
                                    onChange={(e) => setDiscount(e.target.value)}
                                    className="w-full border px-3 py-2 rounded"
                                />
                            </div>
                            <div>
                                <label>Expiry Date</label>
                                <input
                                    type="date"
                                    value={expiryDate}
                                    onChange={(e) => setExpiryDate(e.target.value)}
                                    className="w-full border px-3 py-2 rounded"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                                <label>Active</label>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-500 text-white px-4 py-2 rounded"
                                >
                                    Add Coupon
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 bg-gray-300 text-black px-4 py-2 rounded"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border overflow-hidden mt-6">
                <table className="w-full table-auto">
                    <thead>
                        <tr className="bg-gray-100 text-left">
                            <th className="px-4 py-2">Code</th>
                            <th className="px-4 py-2">Discount</th>
                            <th className="px-4 py-2">Expiry</th>
                            <th className="px-4 py-2">Status</th>
                            <th className="px-4 py-2 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedCoupons.map((c) => (
                            <tr
                                key={c.id}
                                className="border-b hover:bg-gray-50 transition-colors"
                            >
                                <td className="px-4 py-2">{c.code}</td>
                                <td className="px-4 py-2">{c.discount}%</td>
                                <td className="px-4 py-2">{c.expiryDate}</td>
                                <td className="px-4 py-2">
                                    {c.isActive ? (
                                        <span className="text-green-600 font-medium">Active</span>
                                    ) : (
                                        <span className="text-red-600 font-medium">Expired</span>
                                    )}
                                </td>
                                <td className="px-4 py-2 text-center">
                                    <button
                                        onClick={() => handleDeleteCoupon(c.id)}
                                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </div>
    )

}
